import type { PrepDetailResponse, PrepListItem, PrepRecoveryInfo } from '../api/prep'

export interface SanitizePrepListItemOptions {
  inMyPrep: boolean
}

const sanitizeEpochSeconds = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value <= 0) {
      return null
    }

    const truncated = Math.trunc(value)
    return truncated > 0 ? truncated : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) {
      return null
    }
    const parsed = Number.parseInt(trimmed, 10)
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed
    }
  }

  return null
}

const sanitizeCategory = (
  value: unknown,
): PrepListItem['categories'][number] | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const data = value as Record<string, unknown>
  const idValue = Number(data.id)

  if (!Number.isFinite(idValue) || idValue <= 0) {
    return null
  }

  return {
    id: Math.floor(idValue),
    name: typeof data.name === 'string' ? data.name : '',
    slug: typeof data.slug === 'string' ? data.slug : '',
  }
}

const sanitizeImage = (value: unknown): PrepListItem['image'] => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const data = value as Record<string, unknown>

  const resolveMediaId = (candidate: unknown): number | undefined => {
    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
      return Math.trunc(candidate)
    }

    if (typeof candidate === 'string') {
      const parsed = Number.parseInt(candidate, 10)
      if (Number.isFinite(parsed) && parsed > 0) {
        return Math.trunc(parsed)
      }
    }

    return undefined
  }

  const mediaId = resolveMediaId(
    (data as { media_id?: unknown }).media_id ??
      (data as { mediaId?: unknown }).mediaId ??
      (data as { id?: unknown }).id,
  )
  const rawThumb =
    (data as { thumb?: unknown }).thumb ??
    (data as { thumbnail?: unknown }).thumbnail ??
    (data as { thumb_url?: unknown }).thumb_url ??
    (data as { url?: unknown }).url
  const thumb = typeof rawThumb === 'string' ? rawThumb.trim() : ''
  const rawAlt =
    (data as { alt?: unknown }).alt ??
    (data as { alt_text?: unknown }).alt_text ??
    (data as { altText?: unknown }).altText
  const alt = typeof rawAlt === 'string' ? rawAlt : ''

  if (mediaId === undefined && thumb === '' && alt === '') {
    return null
  }

  const normalized: NonNullable<PrepListItem['image']> = {}

  if (mediaId !== undefined) {
    normalized.media_id = mediaId
  }

  if (thumb) {
    normalized.thumb = thumb
  }

  if (alt) {
    normalized.alt = alt
  }

  const sizes = (data as { sizes?: unknown }).sizes
  if (sizes && typeof sizes === 'object') {
    const map = sizes as Record<string, unknown>
    const normalizedSizes: NonNullable<NonNullable<PrepListItem['image']>['sizes']> = {}

    const attachSize = (key: keyof typeof normalizedSizes, value: unknown) => {
      if (typeof value === 'string' && value.trim()) {
        normalizedSizes[key] = value.trim()
      }
    }

    attachSize('thumb', map.thumb ?? map.thumbnail ?? map.thumb_url)
    attachSize('medium', map.medium)
    attachSize('large', map.large)
    attachSize('full', map.full ?? map.url)

    if (Object.keys(normalizedSizes).length > 0) {
      normalized.sizes = normalizedSizes
    }
  }

  return normalized
}

const sanitizeRecoveryInfo = (value: unknown): PrepRecoveryInfo | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const data = value as Record<string, unknown>
  const type = data.type
  if (type !== 'dismissed' && type !== 'used' && type !== 'dismissed_all') {
    return null
  }

  const eventAt = typeof data.event_at === 'string' ? data.event_at : null
  const eventAtIso =
    typeof data.event_at_iso_utc === 'string' && data.event_at_iso_utc.trim()
      ? data.event_at_iso_utc.trim()
      : null
  const eventAtEpoch = sanitizeEpochSeconds(data.event_at_epoch)
  const usedDate = typeof data.used_date === 'string' ? data.used_date : null

  return {
    type,
    event_at: eventAt,
    event_at_iso_utc: eventAtIso ?? undefined,
    event_at_epoch: eventAtEpoch ?? undefined,
    used_date: usedDate ?? undefined,
  }
}

export const sanitizePrepListItem = (
  source: unknown,
  { inMyPrep }: SanitizePrepListItemOptions,
): PrepListItem | null => {
  if (!source || typeof source !== 'object') {
    return null
  }

  const data = source as Record<string, unknown>
  const idValue = Number(data.id)

  if (!Number.isFinite(idValue) || idValue <= 0) {
    return null
  }

  const id = Math.floor(idValue)

  const categories = Array.isArray(data.categories)
    ? data.categories
        .map((category) => sanitizeCategory(category))
        .filter((category): category is PrepListItem['categories'][number] => category !== null)
    : []

  let primaryCategoryId: number | null = null

  if (Number.isFinite((data as { primary_category_id?: unknown }).primary_category_id)) {
    const candidate = Number((data as { primary_category_id?: unknown }).primary_category_id)
    if (candidate > 0) {
      primaryCategoryId = Math.floor(candidate)
    }
  }

  const primaryFromField = sanitizeCategory((data as { primary_category?: unknown }).primary_category ?? null)
  let primaryCategory = primaryFromField

  if (primaryCategoryId !== null) {
    primaryCategory = categories.find((category) => category.id === primaryCategoryId) ?? null

    if (!primaryCategory && primaryFromField && primaryFromField.id === primaryCategoryId) {
      primaryCategory = primaryFromField
    }
  } else if (primaryFromField) {
    primaryCategory = categories.find((category) => category.id === primaryFromField.id) ?? primaryFromField
    primaryCategoryId = primaryCategory?.id ?? null
  }

  return {
    id,
    headline: typeof data.headline === 'string' ? data.headline : '',
    tease: typeof data.tease === 'string' ? data.tease : null,
    preview:
      typeof (data as { preview?: unknown }).preview === 'string'
        ? (((data as { preview?: unknown }).preview as string).trim() || null)
        : null,
    categories,
    primary_category: primaryCategory ?? null,
    primary_category_id: primaryCategoryId,
    event_date:
      typeof (data as { event_date?: unknown }).event_date === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test((data as { event_date: string }).event_date)
        ? (data as { event_date: string }).event_date
        : null,
    published_at: typeof data.published_at === 'string' ? data.published_at : null,
    published_at_iso_utc:
      typeof (data as { published_at_iso_utc?: unknown }).published_at_iso_utc === 'string' &&
      ((data as { published_at_iso_utc?: unknown }).published_at_iso_utc as string).trim().length > 0
        ? ((data as { published_at_iso_utc?: unknown }).published_at_iso_utc as string).trim()
        : null,
    published_at_epoch: sanitizeEpochSeconds(
      (data as { published_at_epoch?: unknown }).published_at_epoch ?? null,
    ),
    is_in_my_prep: inMyPrep,
    short_story_present: Boolean(data.short_story_present),
    short_story_preview:
      typeof data.short_story_preview === 'string' ? data.short_story_preview : null,
    short_story_preferred:
      typeof data.short_story_preferred === 'string' ? data.short_story_preferred : null,
    short_story_neutral:
      typeof data.short_story_neutral === 'string' ? data.short_story_neutral : null,
    image: sanitizeImage((data as { image?: unknown }).image ?? null),
    recovery: sanitizeRecoveryInfo((data as { recovery?: unknown }).recovery ?? null),
  }
}

export const coerceDetailToPrepListItem = (
  detail: PrepDetailResponse,
  options: { fallbackInMyPrep?: boolean } = {},
): PrepListItem => {
  const inMyPrep = Boolean(
    (detail as { is_in_my_prep?: unknown }).is_in_my_prep ?? options.fallbackInMyPrep ?? false,
  )

  const sanitized = sanitizePrepListItem(detail, { inMyPrep })

  if (!sanitized) {
    throw new Error('Unable to normalize prep detail response.')
  }

  return sanitized
}
